// ==========================================================
// Goals.js — Updated with Mobile Input Fix (Solution A)
// Inputs + buttons stack vertically only below 768px
// Desktop + Tablet remain horizontal
// Includes comments explaining every change
// ==========================================================

import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  arrayUnion,
  serverTimestamp,
} from "firebase/firestore";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./Goals.css";
import { onAuthStateChanged } from "firebase/auth";

function Goals() {
  const [user, setUser] = useState(null);
  const [goals, setGoals] = useState([]);
  const [newGoal, setNewGoal] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGoal, setExpandedGoal] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [noteDate, setNoteDate] = useState("");

  /* --------------------------------------------
     AUTH LISTENER
  -------------------------------------------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (current) => {
      setUser(current);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  /* --------------------------------------------
     FETCH GOALS IN REAL TIME
  -------------------------------------------- */
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "goals"), where("uid", "==", user.uid));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setGoals(data);
    });
    return () => unsub();
  }, [user]);

  /* --------------------------------------------
     ADD A GOAL
  -------------------------------------------- */
  const addGoal = async () => {
    if (!newGoal.trim()) return alert("Enter a goal");
    if (!user) return alert("Please sign in");

    await addDoc(collection(db, "goals"), {
      title: newGoal.trim(),
      done: false,
      createdAt: serverTimestamp(),
      uid: user.uid,
      dueDate: dueDate || null,
      completedAt: null,
      notes: [],
    });

    setNewGoal("");
    setDueDate("");
  };

  /* --------------------------------------------
     TOGGLE COMPLETION
  -------------------------------------------- */
  const toggleDone = async (id, done) => {
    const ref = doc(db, "goals", id);
    await updateDoc(ref, {
      done: !done,
      completedAt: !done ? new Date().toISOString() : null,
    });
  };

  /* --------------------------------------------
     DELETE A GOAL
  -------------------------------------------- */
  const deleteGoal = async (id) => {
    await deleteDoc(doc(db, "goals", id));
  };

  /* --------------------------------------------
     ADD PROGRESS NOTE
  -------------------------------------------- */
  const addNote = async (goalId) => {
    if (!newNote.trim() || !noteDate)
      return alert("Write a note and select a date");

    const ref = doc(db, "goals", goalId);
    await updateDoc(ref, {
      notes: arrayUnion({
        text: newNote.trim(),
        date: noteDate,
        createdAt: new Date().toISOString(),
      }),
    });

    setNewNote("");
    setNoteDate("");
  };

  /* --------------------------------------------
     EXPAND/COLLAPSE NOTES
  -------------------------------------------- */
  const toggleExpand = (id) =>
    setExpandedGoal((prev) => (prev === id ? null : id));

  /* --------------------------------------------
     GOAL COLOR (SUCCESS / LATE / EARLY)
  -------------------------------------------- */
  const getGoalColor = (goal) => {
    if (!goal.done) return "var(--muted)";
    if (!goal.dueDate || !goal.completedAt) return "var(--primary)";

    const due = new Date(goal.dueDate);
    const completed = new Date(goal.completedAt);

    if (completed < due) return "#00e676"; // green
    if (completed.toDateString() === due.toDateString()) return "#ffc107"; // yellow
    return "#ff1744"; // red
  };

  /* --------------------------------------------
     CALENDAR DOTS
  -------------------------------------------- */
  const tileContent = ({ date, view }) => {
    if (view !== "month") return null;

    const createdMatch = goals.some((g) => {
      if (!g.createdAt) return false;
      const d = g.createdAt.toDate ? g.createdAt.toDate() : new Date(g.createdAt);
      return d.toDateString() === date.toDateString();
    });

    const dueGoal = goals.find(
      (g) => g.dueDate && new Date(g.dueDate).toDateString() === date.toDateString()
    );

    if (dueGoal) {
      return (
        <div
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: getGoalColor(dueGoal),
            margin: "2px auto",
          }}
        ></div>
      );
    }

    if (createdMatch) {
      return (
        <div
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: "var(--muted)",
            margin: "2px auto",
          }}
        ></div>
      );
    }

    return null;
  };

  /* --------------------------------------------
     FILTER BY DAY CLICK
  -------------------------------------------- */
  const handleDateClick = (date) => {
    if (selectedDate && new Date(selectedDate).toDateString() === date.toDateString()) {
      setSelectedDate(null);
    } else {
      setSelectedDate(date);
    }
  };

  const filteredGoals = selectedDate
    ? goals.filter(
        (g) =>
          (g.createdAt &&
            (g.createdAt.toDate
              ? g.createdAt.toDate().toDateString() === selectedDate.toDateString()
              : new Date(g.createdAt).toDateString() === selectedDate.toDateString())) ||
          (g.dueDate &&
            new Date(g.dueDate).toDateString() === selectedDate.toDateString())
      )
    : goals;

  return (
    <>
      <Helmet>
        <title>Goals – ReflectRise</title>
        <link rel="canonical" href="https://reflectrise.co.uk/goals" />
      </Helmet>

      <div
        className="page-container"
        style={{ background: "var(--bg)", color: "var(--text)" }}
      >
        <div
          className="goals-card"
          style={{
            background: "var(--card)",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            color: "var(--text)",
          }}
        >
          <h2 style={{ textAlign: "center", color: "var(--primary)" }}>🎯 Goals</h2>
          <p style={{ textAlign: "center", color: "var(--muted)" }}>
            Track your goals, progress, and milestones.
          </p>

          {/* ----------------------------------------------------
              UPDATED INPUT LAYOUT (Solution A)
              Desktop/tablet = horizontal flex layout
              Mobile (<768px) = vertical stack (handled in CSS)
          ----------------------------------------------------- */}
          <div className="goal-input-wrapper">
            <input
              type="text"
              placeholder="What's your next goal?"
              value={newGoal}
              onChange={(e) => setNewGoal(e.target.value)}
              className="input"
              style={{ background: "var(--card)", color: "var(--text)" }}
            />

            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="input"
            />

            <button className="btn" onClick={addGoal}>
              Add Goal
            </button>
          </div>

          {/* ----------------------------------------- */}
          {/* CALENDAR */}
          {/* ----------------------------------------- */}
          <div className="calendar-container" style={{ marginTop: "20px" }}>
            <Calendar
              onClickDay={handleDateClick}
              value={selectedDate}
              tileContent={tileContent}
            />
            {selectedDate && (
              <p style={{ textAlign: "center", marginTop: "8px", color: "var(--muted)" }}>
                Showing goals for <strong>{selectedDate.toDateString()}</strong> — click again to reset.
              </p>
            )}
          </div>

          {/* ----------------------------------------- */}
          {/* GOALS LIST */}
          {/* ----------------------------------------- */}
          {loading ? (
            <p>Loading goals...</p>
          ) : !user ? (
            <p>Please sign in to view your goals.</p>
          ) : filteredGoals.length === 0 ? (
            <p>No goals for this day.</p>
          ) : (
            <div className="goal-list" style={{ marginTop: "20px" }}>
              {filteredGoals.map((goal) => {
                const color = getGoalColor(goal);

                return (
                  <div
                    key={goal.id}
                    className="goal-item"
                    style={{
                      backgroundColor: "var(--card)",
                      borderLeft: `4px solid ${color}`,
                      borderRadius: "10px",
                      padding: "10px 15px",
                      marginBottom: "12px",
                      transition: "0.3s",
                    }}
                  >
                    <div
                      className="goal-header"
                      onClick={() => toggleExpand(goal.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <p>
                        {goal.title}{" "}
                        <span className="goal-due" style={{ color: "var(--muted)" }}>
                          •{" "}
                          {goal.dueDate
                            ? `Due ${new Date(goal.dueDate).toLocaleDateString()}`
                            : "No due date"}
                        </span>
                      </p>

                      <div className="goal-buttons">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDone(goal.id, goal.done);
                          }}
                          className="btn"
                          style={{
                            backgroundColor: color,
                            color: "#fff",
                            border: "none",
                            borderRadius: "6px",
                            padding: "5px 10px",
                            marginRight: "6px",
                          }}
                        >
                          {goal.done ? "Undo" : "Mark Done"}
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteGoal(goal.id);
                          }}
                          className="btn secondary"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {expandedGoal === goal.id && (
                      <div
                        className="notes-section"
                        style={{
                          marginTop: "10px",
                          padding: "10px",
                          borderRadius: "8px",
                          backgroundColor: "var(--panel)",
                        }}
                      >
                        <h4 style={{ color: "var(--text)" }}>Progress Notes</h4>

                        <div className="note-input" style={{ marginBottom: "10px" }}>
                          <textarea
                            placeholder="Write your progress..."
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            className="input"
                          />

                          <input
                            type="date"
                            value={noteDate}
                            onChange={(e) => setNoteDate(e.target.value)}
                            className="input"
                          />

                          <button className="btn" onClick={() => addNote(goal.id)}>
                            Add Note
                          </button>
                        </div>

                        {goal.notes?.length > 0 ? (
                          goal.notes
                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                            .map((note, i) => (
                              <div
                                key={i}
                                className="card"
                                style={{
                                  backgroundColor: "var(--card)",
                                  borderLeft: `3px solid ${color}`,
                                  padding: "8px",
                                  borderRadius: "5px",
                                  marginBottom: "6px",
                                }}
                              >
                                <p>
                                  <strong>
                                    {new Date(note.date).toLocaleDateString()}:
                                  </strong>{" "}
                                  {note.text}
                                </p>
                              </div>
                            ))
                        ) : (
                          <p style={{ color: "var(--muted)" }}>
                            No notes yet for this goal.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Goals;
